import { useMutation, useQuery } from '@apollo/client';
import { CircularProgress } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { Button } from '@ovComponents/2-component/button/button';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { ColorPickerField } from '@ovComponents/2-component/colorPickerField/colorPickerField';
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext, usePermissions } from '@providers/userContextProvider';
import { processThemeTokens } from '@utils/themeTokensUtils';
import { cloneDeep, set } from 'lodash-es';
import type { ChangeEvent, CSSProperties } from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  FetchOrganizationThemeTokens,
  UpdateOrganizationThemeTokens,
} from './textDesigner.queries';
import {
  COMMON_FONT_FAMILIES,
  type FieldErrorKey,
  formatFontFamilyString,
  getExampleTextForVariant,
  isFontOptionEqual,
  type OrganizationState,
  parseFontFamilyString,
  TYPOGRAPHY_VARIANTS,
  type TypographyVariant,
  VALIDATION_MESSAGE_KEYS,
  validateField,
} from './utils';

export const TextDesignerSettings = () => {
  const { activeOrganization } = useContext(UserContext);
  const { showToast } = useGlobalToast();
  const { t } = useTranslation(['settings', 'shared']);
  const { permissions } = usePermissions();
  const { sys, comp } = useThemeTokens();

  const [organization, setOrganization] = useState<OrganizationState>({
    themeTokens: {},
  });
  const [selectedVariant, setSelectedVariant] = useState<string>(
    TYPOGRAPHY_VARIANTS[0],
  );
  const [previewStyles, setPreviewStyles] = useState<TypographyVariant>({});
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<FieldErrorKey, boolean>>
  >({});
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
  const applyConfirmTitleId = useId();
  const applyConfirmDescId = useId();
  const isInitializedRef = useRef<boolean>(false);
  const getValidationMessage = (key: FieldErrorKey): string =>
    t(`textDesigner.${VALIDATION_MESSAGE_KEYS[key]}`);

  const { data, loading, error, refetch } = useQuery(
    FetchOrganizationThemeTokens,
    {
      variables: { organizationId: activeOrganization?.id ?? '' },
      skip: !activeOrganization?.id,
    },
  );

  const [updateOrganization, { loading: updateLoading }] = useMutation(
    UpdateOrganizationThemeTokens,
    {
      onCompleted: () => {
        showToast({
          message: t('shared:successfullySaved'),
          severity: 'success',
        });
        refetch();
      },
      onError: (error) => {
        showToast({
          message: error.message,
          severity: 'error',
        });
      },
    },
  );

  // When active organization changes, refetch and reset so the page reloads with new org data
  useEffect(() => {
    if (!activeOrganization?.id) return;
    isInitializedRef.current = false;
    setSelectedVariant(TYPOGRAPHY_VARIANTS[0]);
    setPreviewStyles({});
    setFieldErrors({});
    refetch();
  }, [activeOrganization?.id, refetch]);

  useEffect(() => {
    if (data?.fetchOrganization?.organization) {
      const org = data.fetchOrganization.organization;
      const next: OrganizationState = {
        id: org.id,
        themeTokens: org.themeTokens ?? undefined,
        useParentThemeTokens: org.useParentThemeTokens ?? undefined,
      };
      setOrganization(next);
    }
  }, [data]);

  const getStylesForVariant = useCallback(
    (variantName: string): TypographyVariant => {
      const typography = organization?.themeTokens?.comp?.typography || {};
      const defaults = comp?.typography?.[variantName] || {};
      const saved = typography[variantName] || {};
      return {
        fontFamily: saved.fontFamily || defaults.fontFamily || '',
        fontSize: saved.fontSize || defaults.fontSize || '',
        fontWeight: saved.fontWeight ?? defaults.fontWeight ?? 400,
        lineHeight: saved.lineHeight || defaults.lineHeight || '',
        letterSpacing: saved.letterSpacing || defaults.letterSpacing || '',
        color:
          saved.color || defaults.color || (sys?.color?.onSurface ?? '#000000'),
      };
    },
    [
      organization?.themeTokens?.comp?.typography,
      comp?.typography,
      sys?.color?.onSurface,
    ],
  );

  // Initialize styles on first load only
  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run on initial organization load
  useEffect(() => {
    if (!isInitializedRef.current && organization?.id) {
      setPreviewStyles(getStylesForVariant(selectedVariant));
      isInitializedRef.current = true;
    }
  }, [organization?.id]);

  // Handle variant selection change
  const handleVariantChange = (newVariant: string) => {
    if (newVariant !== selectedVariant) {
      setPreviewStyles(getStylesForVariant(newVariant));
      setFieldErrors({});
      setSelectedVariant(newVariant);
    }
  };

  // Handle field changes
  const handleFieldChange = (
    field: keyof TypographyVariant,
    value: string | number,
  ) => {
    setPreviewStyles((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear that field's error when value changes so we re-validate on next blur
    if (
      field === 'fontSize' ||
      field === 'fontWeight' ||
      field === 'letterSpacing' ||
      field === 'lineHeight' ||
      field === 'color'
    ) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field as FieldErrorKey];
        return next;
      });
    }
  };

  const validateFieldOnBlur = (field: FieldErrorKey) => {
    const value = previewStyles[field];
    const invalidKey = validateField(field, value);

    setFieldErrors((prev) =>
      invalidKey
        ? { ...prev, [invalidKey]: true }
        : (() => {
            const next = { ...prev };
            delete next[field];
            return next;
          })(),
    );
  };

  // Apply and save changes (called after user confirms in modal)
  const handleApply = () => {
    setApplyConfirmOpen(false);
    const updatedThemeTokens = set(
      cloneDeep(organization.themeTokens ?? {}),
      ['comp', 'typography', selectedVariant],
      { ...previewStyles },
    );

    updateOrganization({
      variables: {
        input: {
          organizationId: activeOrganization?.id ?? '',
          themeTokens: processThemeTokens(updatedThemeTokens),
        },
      },
    });
  };

  const handleApplyClick = () => setApplyConfirmOpen(true);

  // Disable Apply until user has changed something from the saved/original state
  const hasChanges = useMemo(() => {
    const baseline = getStylesForVariant(selectedVariant);
    const n = (v: number | undefined) =>
      v === undefined || v === null ? 400 : Number(v);
    return (
      (previewStyles.fontFamily ?? '') !== (baseline.fontFamily ?? '') ||
      (previewStyles.fontSize ?? '') !== (baseline.fontSize ?? '') ||
      n(previewStyles.fontWeight) !== n(baseline.fontWeight) ||
      (previewStyles.lineHeight ?? '') !== (baseline.lineHeight ?? '') ||
      (previewStyles.letterSpacing ?? '') !== (baseline.letterSpacing ?? '') ||
      (previewStyles.color ?? '') !== (baseline.color ?? '')
    );
  }, [getStylesForVariant, selectedVariant, previewStyles]);

  // Only apply values that are valid; use inherit for any field with wrong format so preview doesn't break
  // Strip trailing semicolon from font-family so it's valid for inline style and preview updates correctly
  const getPreviewStyle = (): CSSProperties => ({
    fontFamily:
      (previewStyles.fontFamily || '').replace(/\s*;\s*$/, '').trim() ||
      'inherit',
    fontSize: fieldErrors.fontSize
      ? 'inherit'
      : previewStyles.fontSize || 'inherit',
    fontWeight: fieldErrors.fontWeight
      ? 'inherit'
      : (previewStyles.fontWeight as CSSProperties['fontWeight']) || 'inherit',
    lineHeight: fieldErrors.lineHeight
      ? 'inherit'
      : previewStyles.lineHeight || 'inherit',
    letterSpacing: fieldErrors.letterSpacing
      ? 'inherit'
      : previewStyles.letterSpacing || 'inherit',
    color: fieldErrors.color ? 'inherit' : previewStyles.color || 'inherit',
  });

  const exampleText = getExampleTextForVariant(selectedVariant);

  return (
    <>
      <Card sx={{ maxWidth: '1440px', overflow: 'visible' }}>
        <CardContent sx={{ p: 3 }}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                minHeight: 280,
                py: 4,
              }}
            >
              <CircularProgress size={32} />
              <Typography variant='bodyMedium' colorVariant='variant'>
                {t('textDesigner.loading')}
              </Typography>
            </Box>
          ) : error ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                minHeight: 280,
                py: 4,
              }}
            >
              <Typography variant='bodyMedium' color='error'>
                {t('textDesigner.loadError')}
              </Typography>
              <Typography variant='bodySmall' colorVariant='variant'>
                {error.message}
              </Typography>
              <Button
                variant='outlined'
                label={t('shared:retry')}
                onClick={() => refetch()}
              />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Left side - Typography Variants List */}
              <Grid item xs={12} md={4}>
                <Typography variant='headingSmall' sx={{ mb: 2 }}>
                  {t('textDesigner.textVariants')}
                </Typography>
                <Typography
                  variant='bodySmall'
                  colorVariant='variant'
                  sx={{ mb: 2 }}
                >
                  {t('textDesigner.changeOrgLevelText')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    maxHeight: '500px',
                    overflowY: 'auto',
                  }}
                >
                  {TYPOGRAPHY_VARIANTS.map((variant) => (
                    <Box
                      key={variant}
                      onClick={() => handleVariantChange(variant)}
                      sx={{
                        padding: '12px 16px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border:
                          selectedVariant === variant
                            ? `2px solid ${sys?.color?.onSurface ?? '#000'}`
                            : `1px solid ${sys?.color?.outlineVariant ?? '#e0e0e0'}`,
                        backgroundColor:
                          selectedVariant === variant
                            ? (sys?.color?.surfaceContainerVariant ?? '#f5f5f5')
                            : 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor:
                            sys?.color?.surfaceContainerVariant ?? '#f5f5f5',
                        },
                      }}
                    >
                      <Typography
                        variant='bodyMedium'
                        sx={{
                          fontWeight: selectedVariant === variant ? 600 : 400,
                        }}
                      >
                        {variant}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>

              {/* Right side - Preview and Form */}
              <Grid item xs={12} md={8}>
                {/* Live Preview Area */}
                <Box
                  sx={{
                    border: `1px dashed ${sys?.color?.outlineVariant ?? '#ccc'}`,
                    borderRadius: '8px',
                    padding: 3,
                    mb: 3,
                    backgroundColor: sys?.color?.surfaceContainer ?? '#fafafa',
                  }}
                >
                  <Box
                    component='h2'
                    key={`preview-title-${previewStyles.fontFamily ?? ''}`}
                    style={getPreviewStyle()}
                    sx={{ mb: 2, margin: 0, marginBottom: 2 }}
                  >
                    {exampleText.title}
                  </Box>
                  <Box
                    component='p'
                    key={`preview-body-${previewStyles.fontFamily ?? ''}`}
                    style={getPreviewStyle()}
                    sx={{ margin: 0 }}
                  >
                    {exampleText.body}
                  </Box>
                </Box>

                {/* Form Fields */}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('textDesigner.fontSize')}
                      value={previewStyles.fontSize || ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleFieldChange('fontSize', e.target.value)
                      }
                      onBlur={() => validateFieldOnBlur('fontSize')}
                      error={!!fieldErrors.fontSize}
                      errorText={
                        fieldErrors.fontSize
                          ? getValidationMessage('fontSize')
                          : undefined
                      }
                      helpText={t('textDesigner.fontSizeHelp')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      fullWidth
                      multiple
                      freeSolo
                      label={t('textDesigner.fontFamily')}
                      helpText={t('textDesigner.fontFamilyHelp')}
                      options={COMMON_FONT_FAMILIES}
                      value={parseFontFamilyString(
                        previewStyles.fontFamily || '',
                      )}
                      onChange={(_: unknown, newValue: string[]) => {
                        handleFieldChange(
                          'fontFamily',
                          formatFontFamilyString(newValue),
                        );
                      }}
                      isOptionEqualToValue={isFontOptionEqual}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ColorPickerField
                      fullWidth
                      label={t('textDesigner.color')}
                      value={
                        previewStyles.color ||
                        sys?.color?.onSurface ||
                        '#000000'
                      }
                      onChange={(value: string) =>
                        handleFieldChange('color', value)
                      }
                      onBlur={() => validateFieldOnBlur('color')}
                      error={!!fieldErrors.color}
                      errorText={
                        fieldErrors.color
                          ? getValidationMessage('color')
                          : undefined
                      }
                      showAlpha={false}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('textDesigner.fontWeight')}
                      value={previewStyles.fontWeight?.toString() || ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.value;
                        const num = val ? parseInt(val, 10) : 400;
                        const safeWeight = Number.isNaN(num)
                          ? (previewStyles.fontWeight ?? 400)
                          : num;
                        handleFieldChange('fontWeight', safeWeight);
                      }}
                      onBlur={() => validateFieldOnBlur('fontWeight')}
                      error={!!fieldErrors.fontWeight}
                      errorText={
                        fieldErrors.fontWeight
                          ? getValidationMessage('fontWeight')
                          : undefined
                      }
                      helpText={t('textDesigner.fontWeightHelp')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('textDesigner.letterSpacing')}
                      value={previewStyles.letterSpacing || ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleFieldChange('letterSpacing', e.target.value)
                      }
                      onBlur={() => validateFieldOnBlur('letterSpacing')}
                      error={!!fieldErrors.letterSpacing}
                      errorText={
                        fieldErrors.letterSpacing
                          ? getValidationMessage('letterSpacing')
                          : undefined
                      }
                      helpText={t('textDesigner.letterSpacingHelp')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('textDesigner.lineHeight')}
                      value={previewStyles.lineHeight || ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleFieldChange('lineHeight', e.target.value)
                      }
                      onBlur={() => validateFieldOnBlur('lineHeight')}
                      error={!!fieldErrors.lineHeight}
                      errorText={
                        fieldErrors.lineHeight
                          ? getValidationMessage('lineHeight')
                          : undefined
                      }
                      helpText={t('textDesigner.lineHeightHelp')}
                    />
                  </Grid>
                </Grid>

                {/* Action Button */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 3,
                  }}
                >
                  <Button
                    disabled={
                      loading ||
                      updateLoading ||
                      !permissions.includes('write:organizations') ||
                      Object.keys(fieldErrors).length > 0 ||
                      !hasChanges
                    }
                    label={t('textDesigner.apply')}
                    onClick={handleApplyClick}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={applyConfirmOpen}
        onClose={() => setApplyConfirmOpen(false)}
        aria-labelledby={applyConfirmTitleId}
        aria-describedby={applyConfirmDescId}
      >
        <DialogTitle
          id={applyConfirmTitleId}
          onClose={() => setApplyConfirmOpen(false)}
        >
          {t('textDesigner.applyConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id={applyConfirmDescId}>
            {t('textDesigner.applyConfirmMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogFooter>
          <Button
            variant='outlined'
            label={t('shared:cancel')}
            onClick={() => setApplyConfirmOpen(false)}
          />
          <Button
            variant='filled'
            label={t('shared:confirm')}
            onClick={handleApply}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};
